import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Flex, Icon, Wrap, WrapItem, Tag } from "@chakra-ui/react";
import { Award, Bot, GraduationCap, Headset, ShieldCheck, Handshake, Quote } from "lucide-react";
import { useText } from "../../i18n/lang";

const TEXT = {
  en: {
    title: "Why Travel Time",
    intro: "Experience, technology and people who answer when you need them.",
    missionLabel: "Our mission",
    mission:
      "To contribute to the success of our clients and partners through bespoke travel management solutions, with relevant content and continuous support.",
    valuesLabel: "Our values",
    values: ["Partnership", "Service excellence", "Integrity", "Team"],
    items: [
      ["IATA accredited consolidator", "Direct issuing and special negotiated fares with the airlines."],
      ["Automation that saves money", "Amadeus robots and our own Repricer, which keeps looking for better fares and reissues tickets at lower cost."],
      ["Experienced consultants", "An average of 12+ years of experience per Travel Time consultant."],
      ["24/7 support", "Emergency assistance day and night for corporate travelers and partner agencies."],
      ["ISO certified", "ISO 9001:2015 quality management and ISO 14001:2015 environmental management."],
      ["Licensed and ANAT member", "Romanian tourism license and member of the National Association of Romanian Travel Agencies."],
    ],
  },
  ro: {
    title: "De ce Travel Time",
    intro: "Experiență, tehnologie și oameni care răspund atunci când ai nevoie de ei.",
    missionLabel: "Misiunea noastră",
    mission:
      "Să contribuim la succesul clienților și partenerilor noștri prin soluții personalizate de management al călătoriilor, cu conținut relevant și suport continuu.",
    valuesLabel: "Valorile noastre",
    values: ["Parteneriat", "Excelență în servicii", "Integritate", "Echipă"],
    items: [
      ["Consolidator acreditat IATA", "Emitere directă și tarife negociate special cu companiile aeriene."],
      ["Automatizări care economisesc bani", "Roboți Amadeus și Repricer-ul propriu, care caută permanent tarife mai bune și reemite biletele la costuri reduse."],
      ["Consultanți cu experiență", "O medie de peste 12 ani de experiență pentru fiecare consultant Travel Time."],
      ["Suport 24/7", "Asistență pentru urgențe zi și noapte, pentru călătorii corporate și agențiile partenere."],
      ["Certificări ISO", "ISO 9001:2015 pentru managementul calității și ISO 14001:2015 pentru managementul mediului."],
      ["Licență de turism și membru ANAT", "Agenție licențiată, membră a Asociației Naționale a Agențiilor de Turism din România."],
    ],
  },
};

const ICONS = [Award, Bot, GraduationCap, Headset, ShieldCheck, Handshake];

const HomeWhy = () => {
  const t = useText(TEXT);

  return (
    <Box py={{ base: 16, md: 24 }} bg="blue.50">
      <Container maxW="container.xl">
        <VStack spacing={4} textAlign="center" mb={{ base: 10, md: 14 }}>
          <Heading as="h2" fontFamily="Montserrat" fontWeight="700" fontSize={{ base: "2xl", md: "4xl" }} color="gray.800">
            {t.title}
          </Heading>
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
            {t.intro}
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
          {/* Misiunea și valorile */}
          <Flex
            direction="column"
            gridRow={{ lg: "span 3" }}
            bgGradient="linear(160deg, #0b1f3a 0%, #1d5fa8 100%)"
            color="white"
            borderRadius="2xl"
            p={{ base: 7, md: 9 }}
          >
            <Icon as={Quote} boxSize={9} color="orange.300" mb={4} />
            <Text fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="blue.200" mb={2}>
              {t.missionLabel}
            </Text>
            <Text fontFamily="Montserrat" fontSize={{ base: "lg", md: "xl" }} lineHeight="1.6" mb={8}>
              {t.mission}
            </Text>
            <Box mt="auto">
              <Text fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="wider" color="blue.200" mb={3}>
                {t.valuesLabel}
              </Text>
              <Wrap spacing={2}>
                {t.values.map((value) => (
                  <WrapItem key={value}>
                    <Tag size="lg" borderRadius="full" bg="whiteAlpha.200" color="white" fontWeight="600">
                      {value}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          </Flex>

          {t.items.map(([title, text], index) => (
            <Flex
              key={title}
              direction={{ base: "row", md: "column" }}
              gap={{ base: 4, md: 0 }}
              bg="white"
              borderRadius="xl"
              p={{ base: 5, md: 7 }}
              borderWidth="1px"
              borderColor="gray.100"
              boxShadow="0 10px 30px -18px rgba(15, 40, 80, 0.35)"
            >
              <Flex
                w={{ base: "44px", md: "48px" }}
                h={{ base: "44px", md: "48px" }}
                flexShrink={0}
                borderRadius="lg"
                bg="blue.500"
                align="center"
                justify="center"
                mb={{ base: 0, md: 5 }}
              >
                <Icon as={ICONS[index]} boxSize={6} color="white" />
              </Flex>
              <Box>
                <Heading as="h3" fontFamily="Montserrat" fontWeight="700" fontSize={{ base: "lg", md: "xl" }} color="gray.800" mb={{ base: 1, md: 2 }}>
                  {title}
                </Heading>
                <Text color="gray.600" fontSize="md" lineHeight="1.6">
                  {text}
                </Text>
              </Box>
            </Flex>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default HomeWhy;
